import { GAME_PARAMS } from '@beat-games/game-config';
import type { CircleState } from '@beat-games/shared-types';

// Danger zone is the outer 20% of the circle radius
const DANGER_ZONE_THRESHOLD = 0.8;

/**
 * Calculate audio intensity (0-1) from game state
 * Intensity is based on elapsed time and circle radius
 */
export function calculateIntensity(elapsed: number, circle: CircleState | null): number {
  // Time-based intensity (primary factor)
  const timeProgress = Math.min(elapsed / GAME_PARAMS.maxDuration, 1);

  // Radius-based intensity (secondary factor)
  let radiusIntensity = 0;
  if (circle) {
    const radiusRange = GAME_PARAMS.startRadius - GAME_PARAMS.endRadius;
    const radiusProgress = (GAME_PARAMS.startRadius - circle.radius) / radiusRange;
    radiusIntensity = Math.min(Math.max(radiusProgress, 0), 1);
  }

  // Combine both factors with time being primary
  return timeProgress * 0.7 + radiusIntensity * 0.3;
}

/**
 * Check if cursor is in the danger zone (outer 20% of circle)
 * Returns true if cursor is within the circle but close to the edge
 */
export function isInDangerZone(
  cursorX: number,
  cursorY: number,
  circle: CircleState
): boolean {
  const dx = cursorX - circle.x;
  const dy = cursorY - circle.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // In danger zone if distance > 80% of radius (but still inside)
  return distance > circle.radius * DANGER_ZONE_THRESHOLD && distance <= circle.radius;
}

/**
 * Get intensity stage description for debugging
 */
export function getIntensityStage(intensity: number): string {
  if (intensity < 0.3) return 'low';
  if (intensity < 0.6) return 'medium';
  if (intensity < 0.8) return 'high';
  return 'max';
}
